import React from "react";
import submit from "submit-form";
import { isEqual, get } from "lodash";

import DateInput from "../../../parts/inputs/date-input";
import NavigationComponent from "./components/NavigationComponent";
import ExercisesList from "./components/ExercisesList";
import HistoryChart from "./components/HistoryChart";
import {
  filterExercises,
  getMovementOptions,
} from "../../../utils/exercise-history";
import { shouldHaveE1RM, hasRpe } from "../../../utils/actuals";
import { AthleteSelect } from "../../../parts/inputs/athlete-select-new";
import { AthleteLinks } from "../../../parts/athlete-links";
import FilterExercisesModal from "./components/FilterExercisesModal";
import FilterChips from "./FilterChips";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { ReportsBackHandler } from "./components/ReportsBackHandler";
import { SearchDropdown } from "../../../parts/inputs/search-dropdown";

const initialFilters = {
  repsFrom: "All",
  repsTo: "All",
  rpeFrom: "All",
  rpeTo: "All",
  sortOrder: "desc",
};
class ExerciseHistory extends React.Component {
  constructor(props, context) {
    super(props, context);
    const { query } = context.locals;
    const selectedValue = query.secondary
      ? `${query.primary}${query.secondary}`
      : query.primary;
    this.state = {
      filters: initialFilters,
      selected: "",
      activeChartLabel: "e1RM",
      querySelected: selectedValue,
    };

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
    this.onFiltersSubmit = this.onFiltersSubmit.bind(this);
    this.renderNoExercises = this.renderNoExercises.bind(this);
    this.getFilterList = this.getFilterList.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.onSort = this.onSort.bind(this);
    this.handleMovementSelect = this.handleMovementSelect.bind(this);
    this.onActiveChartChange = this.onActiveChartChange.bind(this);
  }

  handleDateChange(date, field) {
    submit("/actions/refresh", {
      method: "GET",
      body: { [field]: date },
    });
  }

  handleClearFilters() {
    this.setState({ filters: initialFilters });
  }

  onFiltersSubmit(filters) {
    this.setState({ filters });
  }
  getFilterList() {
    let filterChips = [];
    const { filters } = this.state;
    if (
      (filters.repsFrom !== "All" && filters.repsFrom > 0) ||
      (filters.repsTo !== "All" && filters.repsTo > 0)
    ) {
      filterChips.push({
        text: `Reps - ${filters.repsFrom} to ${filters.repsTo}`,
        id: "reps",
      });
    }
    if (
      (filters.rpeFrom !== "All" && filters.rpeFrom > 0) ||
      (filters.rpeTo !== "All" && filters.rpeTo > 0)
    ) {
      filterChips.push({
        text: `RPE - ${filters.rpeFrom} to ${filters.rpeTo}`,
        id: "rpe",
      });
    }
    if (filters.loadFrom > 0 || filters.loadTo > 0) {
      filterChips.push({
        text: `Load - ${filters.loadFrom} to ${filters.loadTo}`,
        id: "load",
      });
    }
    return filterChips;
  }
  clearFilter(id) {
    this.setState({
      filters: {
        ...this.state.filters,
        [`${id}From`]: id !== "load" ? "All" : "",
        [`${id}To`]: id !== "load" ? "All" : "",
      },
    });
  }
  onSort() {
    this.setState({
      filters: {
        ...this.state.filters,
        sortOrder: this.state.filters.sortOrder === "asc" ? "desc" : "asc",
      },
    });
  }
  renderNoExercises() {
    const filtersSet = !isEqual(this.state.filters, initialFilters);

    return (
      <div className="no-data-wrapper">
        <span>
          No exercises match your reporting period
          {filtersSet ? " or filters" : ""}
        </span>
      </div>
    );
  }
  handleMovementSelect(value) {
    this.setState({ selected: value.label });
    if (value) {
      submit("/actions/refresh", {
        method: "GET",
        body: {
          primary: value.primary,
          secondary: value.secondary,
          type: value.type,
        },
      });
    }
  }
  onActiveChartChange(chartLabel) {
    this.setState({ activeChartLabel: chartLabel });
  }
  render() {
    const { locals } = this.context;
    const { filters } = this.state;
    const {
      activeUser,
      activeUserToRequest,
      athlete,
      query,
      pathname,
      exerciseHistory: { exercises },
      fromMetric,
      withWeight,
    } = locals;
    const { primary } = query;
    const { units } = activeUser;
    const { exerciseLibrary } = activeUserToRequest;
    const intensityMeasure =
      get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
        ? "RPE"
        : "RiR";
    const isRiR = intensityMeasure === "RiR";

    const filteredExercises = filterExercises(
      exercises,
      filters,
      primary,
      units
    );
    const hideVelocitySettings = activeUser.hideVelocitySettings !== false;

    const hasRepsLoad =
      filteredExercises.length && shouldHaveE1RM(filteredExercises[0]);
    const showFilters = hasRepsLoad && hasRpe(filteredExercises[0]);
    const movementOptions = getMovementOptions(exerciseLibrary);

    // Preset value will be the one selected on the first load, could be
    // coming from the query or just a fallback one as default
    const presetValue =
      this.state.selected ||
      (this.state.querySelected &&
        movementOptions?.find(
          (option) => option?.value === this.state.querySelected
        )?.label) ||
      movementOptions[0].label;

    return (
      <main role="reports-wrapper">
        <div className="athlete-section-header only-show-mobile">
          <AthleteLinks
            athlete={athlete}
            pathName={pathname}
            classNames={"only-show-mobile"}
            query={query}
          />
          <div className="athlete-select dropdown-athlete">
            <div
              className="avatar"
              style={{
                backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
              }}
            />
            <AthleteSelect locals={locals} />
          </div>
        </div>
        <NavigationComponent
          query={query}
          text="Calendar Day View"
          pathName={pathname}
          classNames="mobile-hidden"
        />
        <article role="report-content" id="exercise-history">
          <header>
            <hgroup>
              <ReportsBackHandler title={"Exercise History"} locals={locals} />
              <h3 className="mobile-hidden">{`Exercise History - ${this.state.activeChartLabel}`}</h3>
              <div>
                <FilterChips
                  filters={this.getFilterList()}
                  clearAllFilters={this.handleClearFilters}
                  clearFilter={this.clearFilter}
                />
                <FilterExercisesModal
                  filters={filters}
                  activeUser={activeUser}
                  athlete={athlete}
                  query={query}
                  onClearFilters={this.handleClearFilters}
                  onFiltersSubmit={this.onFiltersSubmit}
                  showFilters={showFilters}
                  withWeight={withWeight}
                  fromMetric={fromMetric}
                />
              </div>
            </hgroup>
            <hgroup>
              <div role="calendar-date-selectors">
                <DateInput
                  name="from"
                  placeholder="Start Date"
                  value={query.from}
                  max={query.to}
                  onChange={(date) => this.handleDateChange(date, "from")}
                />
                <span>to</span>
                <DateInput
                  name="to"
                  placeholder="End Date"
                  value={query.to}
                  min={query.from}
                  onChange={(date) => this.handleDateChange(date, "to")}
                />
              </div>
              <SearchDropdown
                value={presetValue}
                placeholder="Select Exercise"
                handleChange={this.handleMovementSelect}
                options={movementOptions}
              />
            </hgroup>
          </header>

          {filteredExercises.length ? (
            <div
              className={`list-chart-wrapper ${
                hasRepsLoad ? "with-chart" : ""
              }`}
            >
              {hasRepsLoad && (
                <HistoryChart
                  exercises={filteredExercises}
                  units={units}
                  fromMetric={fromMetric}
                  from={query.from}
                  to={query.to}
                  activeUser={activeUser}
                  athlete={athlete}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  onActiveChartChange={this.onActiveChartChange}
                />
              )}
              <ExercisesList
                exercises={filteredExercises}
                units={units}
                fromMetric={fromMetric}
                withWeight={withWeight}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                onSort={this.onSort}
                hideVelocitySettings={hideVelocitySettings}
              />
            </div>
          ) : (
            this.renderNoExercises()
          )}
        </article>
      </main>
    );
  }
}

export default ExerciseHistory;
